import { modelState, ModelStateList } from "@/utils/modelState";
import { toDateTimeString } from "@/utils/utils";
import { ScheduleEvent } from "../ScheduleWrapperComponent";

export namespace BookingPeriodConditionTypes {

    export const BookingPeriodConditionType = Symbol("BookingPeriodConditionType");
    export interface BookingPeriodConditionType {
        [BookingPeriodConditionType]: boolean;
        version: modelState<number>;
        dispose: () => void;
    }

    export interface BookingPeriodConditionTypeDto {
        $type: string;
        Value: BookingPeriodConditionTypeValueDto;
    }

    export interface BookingPeriodConditionTypeValueDto {
        //
    }

    export function serializeBookingPeriodConditionType(action: BookingPeriodConditionType): BookingPeriodConditionTypeDto {
        if (!action) {
            return null;
        }

        for (let bookingPeriodConditionType of bookingPeriodConditionTypes) {
            if (bookingPeriodConditionType.isInstanceOf(action)) {
                return {
                    $type: bookingPeriodConditionType.key,
                    Value: bookingPeriodConditionType.serialize(action)
                };
            }
        }

        throw new Error("Invalid status type");
    }

    export function deserializeBookingPeriodConditionType(dto: BookingPeriodConditionTypeDto): BookingPeriodConditionType {
        if (!dto) {
            return null;
        }

        for (let bookingPeriodConditionType of bookingPeriodConditionTypes) {
            if (bookingPeriodConditionType.key === dto.$type) {
                return bookingPeriodConditionType.deserialize(dto.Value);
            }
        }

        throw new Error("Invalid status type");
    }

    export function getBookingPeriodConditionTypeName(status: BookingPeriodConditionType): string {

        for (let bookingPeriodConditionType of bookingPeriodConditionTypes) {
            if (bookingPeriodConditionType.isInstanceOf(status)) {
                return bookingPeriodConditionType.name;
            }
        }

        throw new Error("Invalid status type");
    }

    export function getBookingPeriodConditionTypeDefinition(status: BookingPeriodConditionType): BookingPeriodConditionTypeDefinition {

        for (let bookingPeriodConditionType of bookingPeriodConditionTypes) {
            if (bookingPeriodConditionType.isInstanceOf(status)) {
                return bookingPeriodConditionType;
            }
        }

        throw new Error("Invalid status type");
    }

    export function createBookingPeriodConditionType(key: string): BookingPeriodConditionType {

        for (let bookingPeriodConditionType of bookingPeriodConditionTypes) {
            if (bookingPeriodConditionType.key === key) {
                return bookingPeriodConditionType.create();
            }
        }

        throw new Error("Invalid bookingPeriodConditionType key");
    }



    export const ScheduleBookingPeriodConditionType = Symbol("ScheduleBookingPeriodConditionType");
    export interface ScheduleBookingPeriodConditionType extends BookingPeriodConditionType {
        [ScheduleBookingPeriodConditionType]: boolean;
        schedule: ModelStateList<ScheduleEvent>;
    }

    export function isScheduleBookingPeriodConditionType(val: BookingPeriodConditionType): val is ScheduleBookingPeriodConditionType {
        return val && (val as ScheduleBookingPeriodConditionType)[ScheduleBookingPeriodConditionType] === true;
    }

    export interface ScheduleBookingPeriodConditionTypeValueDto extends BookingPeriodConditionTypeValueDto {
        events: ScheduleEvent[];
    }

    export const ScheduleBookingPeriodConditionTypeDefinition: BookingPeriodConditionTypeDefinitionT<ScheduleBookingPeriodConditionType, ScheduleBookingPeriodConditionTypeValueDto> =
    {
        key: "Schedule",
        name: "Schedule",
        isInstanceOf: (bookingPeriodConditionType: BookingPeriodConditionType) => isScheduleBookingPeriodConditionType(bookingPeriodConditionType),
        create: function (this: BookingPeriodConditionTypeDefinition, skipHydration: boolean = false): ScheduleBookingPeriodConditionType {
            const bookingPeriodConditionType = {
                [BookingPeriodConditionType]: true,
                [ScheduleBookingPeriodConditionType]: true,
                version: modelState(0),
                schedule: new ModelStateList<ScheduleEvent>([]),
                dispose: () => { }
            };

            if (!skipHydration) {
                this.hydrate(bookingPeriodConditionType, true);
            }

            return bookingPeriodConditionType;
        },
        hydrate: function (this: BookingPeriodConditionTypeDefinition, bookingPeriodConditionType: ScheduleBookingPeriodConditionType, recursive: boolean = false) {
            const unsubscribeList = bookingPeriodConditionType.schedule.subscribe(() => {
                bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
            });

            const oldDispose = bookingPeriodConditionType.dispose;

            bookingPeriodConditionType.dispose = () => {
                unsubscribeList();

                oldDispose();
            };
        },
        serialize: (bookingPeriodConditionType: ScheduleBookingPeriodConditionType): ScheduleBookingPeriodConditionTypeValueDto => {
            return {
                events: bookingPeriodConditionType.schedule.toArray()
            };
        },
        deserialize: function (this: BookingPeriodConditionTypeDefinition, dto: ScheduleBookingPeriodConditionTypeValueDto) {
            const bookingPeriodConditionType = this.create() as ScheduleBookingPeriodConditionType;

            dto.events.forEach(event => bookingPeriodConditionType.schedule.add(event));

            bookingPeriodConditionType.schedule.commitChange();

            return bookingPeriodConditionType;
        }
    };



    export type DateComparisonOperator = "gt" | "gte" | "lt" | "lte" | "eq" | "neq";

    export const DateBookingPeriodConditionType = Symbol("DateBookingPeriodConditionType");
    export interface DateBookingPeriodConditionType extends BookingPeriodConditionType {
        [DateBookingPeriodConditionType]: boolean;
        version: modelState<number>;
        operator: modelState<DateComparisonOperator>;
        value: modelState<Date>;
        dispose: () => void;
    }

    export function isDateBookingPeriodConditionType(val: BookingPeriodConditionType): val is DateBookingPeriodConditionType {
        return val && (val as DateBookingPeriodConditionType)[DateBookingPeriodConditionType] === true;
    }

    export interface DateBookingPeriodConditionTypeValueDto extends BookingPeriodConditionTypeValueDto {
        operator: DateComparisonOperator;
        value: string;
    }

    export const DateBookingPeriodConditionTypeDefinition: BookingPeriodConditionTypeDefinitionT<DateBookingPeriodConditionType, DateBookingPeriodConditionTypeValueDto> =
    {
        key: "Date",
        name: "Date",
        isInstanceOf: (bookingPeriodConditionType: BookingPeriodConditionType) => isDateBookingPeriodConditionType(bookingPeriodConditionType),
        create: function (this: BookingPeriodConditionTypeDefinition, skipHydration: boolean = false): DateBookingPeriodConditionType {
            const bookingPeriodConditionType = {
                [BookingPeriodConditionType]: true,
                [DateBookingPeriodConditionType]: true,
                version: modelState(0),
                operator: modelState<DateComparisonOperator>("gt"),
                value: modelState(new Date()),
                dispose: () => { }
            };

            if (!skipHydration) {
                this.hydrate(bookingPeriodConditionType, true);
            }

            return bookingPeriodConditionType;
        },
        hydrate: function (this: BookingPeriodConditionTypeDefinition, bookingPeriodConditionType: DateBookingPeriodConditionType, recursive: boolean = false) {
            const unsubscribeList = [
                bookingPeriodConditionType.operator.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                }),
                bookingPeriodConditionType.value.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                })
            ];

            const oldDispose = bookingPeriodConditionType.dispose;

            bookingPeriodConditionType.dispose = () => {
                unsubscribeList.forEach(unsubscribe => unsubscribe());

                oldDispose();
            };
        },
        serialize: (bookingPeriodConditionType: DateBookingPeriodConditionType): DateBookingPeriodConditionTypeValueDto => {
            return {
                operator: bookingPeriodConditionType.operator.value,
                value: toDateTimeString(bookingPeriodConditionType.value.value)
            };
        },
        deserialize: function (this: BookingPeriodConditionTypeDefinition, dto: DateBookingPeriodConditionTypeValueDto) {
            const bookingPeriodConditionType = this.create() as DateBookingPeriodConditionType;

            bookingPeriodConditionType.operator.setValue(dto.operator);
            bookingPeriodConditionType.operator.commit();

            bookingPeriodConditionType.value.setValue(new Date(dto.value));
            bookingPeriodConditionType.value.commit();

            return bookingPeriodConditionType;
        }
    };

    export type YearlyDateComparisonOperator = "gt" | "gte" | "lt" | "lte" | "eq" | "neq";

    export const YearlyDateBookingPeriodConditionType = Symbol("YearlyDateBookingPeriodConditionType");
    export interface YearlyDateBookingPeriodConditionType extends BookingPeriodConditionType {
        [YearlyDateBookingPeriodConditionType]: boolean;
        version: modelState<number>;
        operator: modelState<YearlyDateComparisonOperator>;
        month: modelState<number>;
        day: modelState<number>;
        dispose: () => void;
    }

    export function isYearlyDateBookingPeriodConditionType(val: BookingPeriodConditionType): val is YearlyDateBookingPeriodConditionType {
        return val && (val as YearlyDateBookingPeriodConditionType)[YearlyDateBookingPeriodConditionType] === true;
    }

    export interface YearlyDateBookingPeriodConditionTypeValueDto extends BookingPeriodConditionTypeValueDto {
        operator: YearlyDateComparisonOperator;
        month: number;
        day: number;
    }

    export const YearlyDateBookingPeriodConditionTypeDefinition: BookingPeriodConditionTypeDefinitionT<YearlyDateBookingPeriodConditionType, YearlyDateBookingPeriodConditionTypeValueDto> =
    {
        key: "YearlyDate",
        name: "Yearly Date",
        isInstanceOf: (bookingPeriodConditionType: BookingPeriodConditionType) => isYearlyDateBookingPeriodConditionType(bookingPeriodConditionType),
        create: function (this: BookingPeriodConditionTypeDefinition, skipHydration: boolean = false): YearlyDateBookingPeriodConditionType {
            const bookingPeriodConditionType = {
                [BookingPeriodConditionType]: true,
                [YearlyDateBookingPeriodConditionType]: true,
                version: modelState(0),
                operator: modelState<YearlyDateComparisonOperator>("gt"),
                month: modelState(12),
                day: modelState(25),
                dispose: () => { }
            };

            if (!skipHydration) {
                this.hydrate(bookingPeriodConditionType, true);
            }

            return bookingPeriodConditionType;
        },
        hydrate: function (this: BookingPeriodConditionTypeDefinition, bookingPeriodConditionType: YearlyDateBookingPeriodConditionType, recursive: boolean = false) {
            const unsubscribeList = [
                bookingPeriodConditionType.operator.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                }),
                bookingPeriodConditionType.month.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                }),
                bookingPeriodConditionType.day.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                })
            ];

            const oldDispose = bookingPeriodConditionType.dispose;

            bookingPeriodConditionType.dispose = () => {
                unsubscribeList.forEach(unsubscribe => unsubscribe());

                oldDispose();
            };
        },
        serialize: (bookingPeriodConditionType: YearlyDateBookingPeriodConditionType): YearlyDateBookingPeriodConditionTypeValueDto => {
            return {
                operator: bookingPeriodConditionType.operator.value,
                month: bookingPeriodConditionType.month.value,
                day: bookingPeriodConditionType.day.value
            };
        },
        deserialize: function (this: BookingPeriodConditionTypeDefinition, dto: YearlyDateBookingPeriodConditionTypeValueDto) {
            const bookingPeriodConditionType = this.create() as YearlyDateBookingPeriodConditionType;

            bookingPeriodConditionType.operator.setValue(dto.operator);
            bookingPeriodConditionType.operator.commit();

            bookingPeriodConditionType.month.setValue(dto.month);
            bookingPeriodConditionType.month.commit();

            bookingPeriodConditionType.day.setValue(dto.day);
            bookingPeriodConditionType.day.commit();

            return bookingPeriodConditionType;
        }
    };



    export const MonthsBookingPeriodConditionType = Symbol("MonthsBookingPeriodConditionType");
    export interface MonthsBookingPeriodConditionType extends BookingPeriodConditionType {
        [MonthsBookingPeriodConditionType]: boolean;
        version: modelState<number>;
        months: modelState<number[]>;
        dispose: () => void;
    }

    export function isMonthsBookingPeriodConditionType(val: BookingPeriodConditionType): val is MonthsBookingPeriodConditionType {
        return val && (val as MonthsBookingPeriodConditionType)[MonthsBookingPeriodConditionType] === true;
    }

    export interface MonthsBookingPeriodConditionTypeValueDto extends BookingPeriodConditionTypeValueDto {
        months: number[];
    }

    export const MonthsBookingPeriodConditionTypeDefinition: BookingPeriodConditionTypeDefinitionT<MonthsBookingPeriodConditionType, MonthsBookingPeriodConditionTypeValueDto> =
    {
        key: "Months",
        name: "Months",
        isInstanceOf: (bookingPeriodConditionType: BookingPeriodConditionType) => isMonthsBookingPeriodConditionType(bookingPeriodConditionType),
        create: function (this: BookingPeriodConditionTypeDefinition, skipHydration: boolean = false): MonthsBookingPeriodConditionType {
            const bookingPeriodConditionType = {
                [BookingPeriodConditionType]: true,
                [MonthsBookingPeriodConditionType]: true,
                version: modelState(0),
                months: modelState<number[]>([]),
                dispose: () => { }
            };

            if (!skipHydration) {
                this.hydrate(bookingPeriodConditionType, false);
            }

            return bookingPeriodConditionType;
        },
        hydrate: function (this: BookingPeriodConditionTypeDefinition, bookingPeriodConditionType: MonthsBookingPeriodConditionType, recursive: boolean = false) {
            const unsubscribeList = [
                bookingPeriodConditionType.months.subscribe(() => {
                    bookingPeriodConditionType.version.setValue(bookingPeriodConditionType.version.value + 1);
                })
            ];

            const oldDispose = bookingPeriodConditionType.dispose;

            bookingPeriodConditionType.dispose = () => {
                unsubscribeList.forEach(unsubscribe => unsubscribe());

                oldDispose();
            };
        },
        serialize: (bookingPeriodConditionType: MonthsBookingPeriodConditionType): MonthsBookingPeriodConditionTypeValueDto => {
            return {
                months: bookingPeriodConditionType.months.value
            };
        },
        deserialize: function (this: BookingPeriodConditionTypeDefinition, dto: MonthsBookingPeriodConditionTypeValueDto) {
            const bookingPeriodConditionType = this.create() as MonthsBookingPeriodConditionType;

            bookingPeriodConditionType.months.setValue(dto.months);
            bookingPeriodConditionType.months.commit();

            return bookingPeriodConditionType;
        }
    };




    interface BookingPeriodConditionTypeDefinition {
        key: string;
        name: string;
        isInstanceOf: (bookingPeriodConditionType: BookingPeriodConditionType) => boolean;
        create: () => BookingPeriodConditionType;
        hydrate: (bookingPeriodConditionType: BookingPeriodConditionType, recursive: boolean) => void;
        serialize: (bookingPeriodConditionType: BookingPeriodConditionType) => BookingPeriodConditionTypeValueDto;
        deserialize: (dto: BookingPeriodConditionTypeValueDto) => BookingPeriodConditionType;
    }

    interface BookingPeriodConditionTypeDefinitionT<T, TDto> extends Omit<BookingPeriodConditionTypeDefinition, 'create' | 'hydrate' | 'serialize' | 'deserialize'> {
        create: () => T;
        hydrate: (bookingPeriodConditionType: T, recursive: boolean) => void;
        serialize: (bookingPeriodConditionType: T) => TDto;
        deserialize: (dto: TDto) => T;
    }

    export const bookingPeriodConditionTypes: BookingPeriodConditionTypeDefinition[] = [
        ScheduleBookingPeriodConditionTypeDefinition,
        DateBookingPeriodConditionTypeDefinition,
        YearlyDateBookingPeriodConditionTypeDefinition,
        MonthsBookingPeriodConditionTypeDefinition
    ]

}

